import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('eduva_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('eduva_token');
      localStorage.removeItem('eduva_user');
      localStorage.removeItem('eduva_school');
      if (!window.location.pathname.includes('/login') && window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
    return Promise.reject(error.response?.data || error);
  }
);

export const streamLessonNote = async (data) => {
  const token = localStorage.getItem('eduva_token');
  return fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/lesson-note`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
};

export const streamChat = async (message, conversationId) => {
  const token = localStorage.getItem('eduva_token');
  return fetch(`${BACKEND_URL}/api/ai/chat/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ message, conversation_id: conversationId }),
  });
};

// Convenience helpers for subscription/plan APIs
export const subscriptionAPI = {
  getPlans: () => api.get('/subscriptions/plans'),
  getGatewayInfo: () => api.get('/subscriptions/gateway-info'),
  getMyPlan: () => api.get('/subscriptions/my-plan'),
  initialize: (data) => api.post('/subscriptions/initialize', data),
  verify: (reference, gateway, transaction_id) => {
    const params = new URLSearchParams({ reference, gateway });
    if (transaction_id) params.set('transaction_id', transaction_id);
    return api.get(`/subscriptions/verify?${params.toString()}`);
  },
};

// Timetable
export const timetableAPI = {
  getClassTimetable: (classId) => api.get(`/timetable/class/${classId}`),
  upsertSlot: (data) => api.post('/timetable/slots', data),
  clearSlot: (classId, day, period) =>
    api.delete(`/timetable/slots?class_id=${classId}&day=${day}&period=${encodeURIComponent(period)}`),
};

// Announcements
export const announcementAPI = {
  list: () => api.get('/announcements'),
  create: (data) => api.post('/announcements', data),
  delete: (id) => api.delete(`/announcements/${id}`),
};

// Student results
export const studentResultsAPI = {
  getMyResults: (termId = '') => api.get(`/exams/my-results${termId ? `?term_id=${termId}` : ''}`),
  getMyReportCard: (termId = '') => api.get(`/exams/my-report-card${termId ? `?term_id=${termId}` : ''}`),
};

// Academic config
export const academicsAPI = {
  getAssessmentConfig: (classId) => api.get(`/academics/classes/${classId}/assessment-config`),
  updateAssessmentConfig: (classId, components) => api.put(`/academics/classes/${classId}/assessment-config`, { components }),
  getTeacherAssignments: (classId = '') => api.get(`/academics/teacher-assignments${classId ? `?class_id=${classId}` : ''}`),
  createTeacherAssignment: (data) => api.post('/academics/teacher-assignments', data),
  deleteTeacherAssignment: (id) => api.delete(`/academics/teacher-assignments/${id}`),
  getMyAssignments: () => api.get('/academics/my-assignments'),
  getStudentComments: (termId = '', classId = '') => api.get(`/academics/student-comments?term_id=${termId}&class_id=${classId}`),
  saveStudentComment: (data) => api.post('/academics/student-comments', data),
};

// Parent fees
export const parentFeesAPI = {
  getInvoices: () => api.get('/fees/parent/invoices'),
};

// Bulk report cards
export const bulkReportAPI = {
  getClassCards: (classId, termId, cumulative = false) =>
    api.get(`/exams/class-report-cards?class_id=${classId}&term_id=${termId}&cumulative=${cumulative}`),
};

export default api;